/**
 * QuickAddFolderSelect — picker for default Quick Add destination folder.
 *
 * Uses SearchableList with dynamic items from useStaticDataImmediate
 * (projects + lists, including later items) plus virtual containers (Inbox, Anyday).
 * Shows proper icons (StaticIcon / DynamicIcon) and area dividers.
 */

import { useMemo, type ReactNode } from "react"
import { useTranslation } from "react-i18next"
import {
  SearchableList,
  type SearchableListItem,
} from "@/app/components/ui/searchable-list"
import { useSetting } from "@/features/settings/hooks/useSetting"
import { useStaticDataImmediate } from "@/features/static-data/useStaticData"
import { ObjectType, ObjectStatus } from "@/db/schema"
import { StaticIcon } from "@/shared/icons/StaticIcon"
import { DynamicIcon } from "@/features/navigation/DynamicIcon"
import type { RegisteredIconType } from "@/shared/icons/iconRegistry"

export const QUICK_ADD_FOLDER_SETTING_ID = "#quick-add-folder"
export const QUICK_ADD_FOLDER_DEFAULT = "inbox"

// ---------------------------------------------------------------------------
// Extended item with icon metadata
// ---------------------------------------------------------------------------

interface FolderItem extends SearchableListItem {
  iconType?: RegisteredIconType
  objectType?: ObjectType
  status?: ObjectStatus
  isLater?: boolean
  isArea?: boolean
  percentage?: number
  unfinished?: number
  /** Custom icon string (pika/emoji) */
  icon?: string
  /** First area item gets a top border as divider */
  isFirstArea?: boolean
}

// ---------------------------------------------------------------------------
// Icon renderer for a FolderItem
// ---------------------------------------------------------------------------

function FolderIcon({ item }: { item: FolderItem }) {
  if (item.iconType) {
    return <StaticIcon type={item.iconType} size={18} mode="color" active />
  }
  if (
    item.objectType === ObjectType.Project ||
    item.objectType === ObjectType.List
  ) {
    return (
      <DynamicIcon
        icon={item.icon}
        type={item.objectType === ObjectType.Project ? "project" : "list"}
        percentage={item.percentage ?? 0}
        unfinished={item.unfinished ?? 0}
        status={item.status ?? ObjectStatus.Unfinished}
        size="small"
      />
    )
  }
  if (item.objectType === ObjectType.Area) {
    return <StaticIcon type="area" size={18} mode="color" />
  }
  return null
}

export function QuickAddFolderSelect() {
  const { t } = useTranslation()
  const { value, setValue } = useSetting(
    QUICK_ADD_FOLDER_SETTING_ID,
    QUICK_ADD_FOLDER_DEFAULT,
  )
  const [staticData] = useStaticDataImmediate()

  const items = useMemo<FolderItem[]>(() => {
    const result: FolderItem[] = [
      { value: "inbox", label: t("Inbox"), iconType: "inbox" },
      { value: "anyday", label: t("Anyday"), iconType: "anyday" },
    ]

    // Use allMenuItems to include later (deferred) projects
    const menuItems = staticData?.allMenuItems ?? []
    let seenArea = false
    for (const item of menuItems) {
      const isArea = item.type === ObjectType.Area
      const isFirstArea = isArea && !seenArea
      if (isArea) seenArea = true

      const percentage =
        item.totalCount > 0
          ? Math.round((item.completedCount / item.totalCount) * 100)
          : 0

      const label = item.isLater
        ? `${item.title}  ·  ${t("Later")}`
        : item.title

      result.push({
        value: item.id,
        label,
        objectType: item.type,
        status: item.status,
        isLater: item.isLater,
        isArea,
        percentage,
        unfinished: item.unfinishedCount,
        icon: item.icon,
        isFirstArea,
      })
    }

    return result
  }, [staticData?.allMenuItems, t])

  // If saved value doesn't match any current item, show Inbox
  const effectiveValue =
    items.some((i) => i.value === value) ? value : QUICK_ADD_FOLDER_DEFAULT

  const renderItem = (item: FolderItem): ReactNode => (
    <span className={`flex items-center gap-2${item.isFirstArea ? " mt-1 border-t border-border pt-2" : ""}${item.isArea ? " font-semibold" : ""}${item.isLater ? " opacity-50" : ""}`}>
      <span className="flex size-5 shrink-0 items-center justify-center">
        <FolderIcon item={item} />
      </span>
      <span className="truncate">{item.isLater ? item.label.split("  ·  ")[0] : item.label}</span>
      {item.isLater && (
        <span className="shrink-0 rounded bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">
          {t("Later")}
        </span>
      )}
    </span>
  )

  const renderTriggerLabel = (selected: FolderItem | undefined): ReactNode => {
    if (!selected) return t("Inbox")
    return (
      <span className="flex items-center gap-2">
        <span className="flex size-5 shrink-0 items-center justify-center">
          <FolderIcon item={selected} />
        </span>
        <span className="truncate">{selected.isLater ? selected.label.split("  ·  ")[0] : selected.label}</span>
      </span>
    )
  }

  return (
    <SearchableList
      items={items}
      value={effectiveValue}
      onValueChange={setValue}
      searchPlaceholder={t("Search…")}
      searchThreshold={6}
      emptyMessage={t("No matching containers")}
      renderItem={renderItem}
      renderTriggerLabel={renderTriggerLabel}
    />
  )
}
